import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { SortType } from "../../../models/SortType";
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Badge,
  Dialog,
  DialogContent,
  DialogActions,
  Backdrop,
} from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import CustomTextField from "../../components/common/textFields/TextField";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../utils/NotificationsContext";
import FilterSidebar from "../../components/common/filter/FilterSidebar";
import { FilterSidebarMenu, FilterMenuType } from "../../../models/FilterMenu";
import { ModuleType } from "../../../models/ModuleType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import UrgentSourcingRequestTable from "../../components/urgent-sourcing/UrgentSourcingRequestTable";
import {
  UrgentSourcingRaisedRequestFilters,
  OrderStatusFilter,
} from "../../../models/UrgentSourcing/UrgentSourcingRequestFilter";
import UrgentSourcingRaiseRequestPopUp from "./UrgentSourcingRaiseRequestPopup";
import {
  UrgentSourcingAddProductForm,
  UrgentSourcingRaisedRequestForm,
} from "../../../models/UrgentSourcing/UrgentSourcing";
import UrgentSourcingAddProductPopup from "./UrgentSOurcingAddProductPopup";
import { useTheme } from "@mui/material/styles";
import FormSmallTextField from "../../components/common/form/FormTextField";

const UrgentSourcingRequestListing: React.FC = () => {
  const { userStore, urgentSourcingStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const searchParams = new URLSearchParams(location.search);

  const parseUrlParams = (): UrgentSourcingRaisedRequestFilters => {
    const parseFilterArray = (param: string | null): FilterValue[] => {
      if (!param) return [];
      return param.split(",").map((item) => {
        const [id, value] = item.split(":");
        return { id: id || "", value: value || "" };
      });
    };

    return {
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      search_text: searchParams.get("search_text") || undefined,
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sort_by: searchParams.get("sort_by") || "created_date",
      sort_type:
        searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
      raised_by: parseFilterArray(searchParams.get("raised_by")),
      catalog_creator: parseFilterArray(searchParams.get("catalog_creator")),
      product_created_by: parseFilterArray(
        searchParams.get("product_created_by")
      ),
      status: parseFilterArray(searchParams.get("status")),
    };
  };

  const initialParams: UrgentSourcingRaisedRequestFilters = parseUrlParams();
  const [filters, setFilters] =
    useState<UrgentSourcingRaisedRequestFilters>(initialParams);
  const [params, setParams] =
    useState<UrgentSourcingRaisedRequestFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );

  const updateSelectedFilters = (
    appliedFilters: UrgentSourcingRaisedRequestFilters
  ) => {
    let temp_filters_count = 0;

    if (appliedFilters?.status && appliedFilters.status.length > 0) {
      temp_filters_count++;
    }

    if (appliedFilters?.raised_by && appliedFilters.raised_by.length > 0) {
      temp_filters_count++;
    }
    if (
      appliedFilters?.catalog_creator &&
      appliedFilters.catalog_creator.length > 0
    ) {
      temp_filters_count++;
    }
    if (
      appliedFilters?.product_created_by &&
      appliedFilters.product_created_by.length > 0
    ) {
      temp_filters_count++;
    }

    urgentSourcingStore.setFilterCount(temp_filters_count);
  };

  useEffect(() => {
    updateSelectedFilters(initialParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = useCallback(
    (
      key: keyof UrgentSourcingRaisedRequestFilters,
      value: UrgentSourcingRaisedRequestFilters[keyof UrgentSourcingRaisedRequestFilters]
    ) => {
      setFilters((prev: UrgentSourcingRaisedRequestFilters) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Raised By",
        placeholder: "Enter Raised Name",
        value: filters.raised_by,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("raised_by", value),
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Catalog Creator By",
        placeholder: "Enter Catalog Creator Name",
        value: filters.catalog_creator,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("catalog_creator", value),
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Product Created By",
        placeholder: "Enter Product Created Name",
        value: filters.product_created_by,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("product_created_by", value),
      },
      {
        moduleType: ModuleType.URGENT_SOURCING,
        fieldType: FilterMenuType.LOCAL_MULTI_SELECT,
        label: "Status",
        list: OrderStatusFilter,
        value: filters?.status?.map((item) => item.id) || [],
        onChange: (selectedValues: string[]) => {
          setFilters((prev) => {
            const selectedOrderStatus = OrderStatusFilter.filter((item) =>
              selectedValues.some((selected) => selected === item.id)
            );

            return {
              ...prev,
              status: selectedOrderStatus,
            };
          });
        },
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleApplyFilters = () => {
    setParams(filters);
    updateSelectedFilters(filters);
    handleFilterToggle();
    urgentSourcingStore.setPopupState("openFilter", false);
  };

  const handleClearFilters = () => {
    const clearedFilters: UrgentSourcingRaisedRequestFilters = {
      raised_by: [],
      catalog_creator: [],
      product_created_by: [],
      status: [],
      page_no: 1,
      search_text: undefined,
      limit: initialParams.limit,
      sort_by: "created_date",
      sort_type: SortType.DESC,
    };
    setFilters(clearedFilters);
    setParams(clearedFilters);
    updateSelectedFilters(clearedFilters);
    handleFilterToggle();
    urgentSourcingStore.setPopupState("openFilter", false);
  };

  useEffect(() => {
    if (
      userStore.hasPermission(EmployeeRoles.ADD_PRODUCT_IN_UNIVERSAL_CATALOG)
    ) {
      urgentSourcingStore.setIsFetchingData(true);
      urgentSourcingStore
        .getUrgentSourcingRequestListing(params)
        .then(() => {})
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          );
        })
        .finally(() => {
          urgentSourcingStore.setIsFetchingData(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = (query: string) => {
    if (query.trim().length > 0) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
      setParams((prev: UrgentSourcingRaisedRequestFilters) => ({
        ...prev,
        search_text: undefined,
        page_no: 1,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev: UrgentSourcingRaisedRequestFilters) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev: UrgentSourcingRaisedRequestFilters) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev: UrgentSourcingRaisedRequestFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: UrgentSourcingRaisedRequestFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleFilterToggle = () => {
    if (urgentSourcingStore.popupState.openFilter) {
      urgentSourcingStore.managePopup("openFilter", "close");
    } else {
      urgentSourcingStore.managePopup("openFilter", "open");
    }
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              if (
                key === "raised_by" ||
                key === "catalog_creator" ||
                key === "product_created_by"
              ) {
                queryParams.append(
                  key,
                  value
                    .map((item: FilterValue) => `${item.id}:${item.value}`)
                    .join(",")
                );
              } else {
                queryParams.append(
                  key,
                  value.map((item: FilterValue) => item.id).join(",")
                );
              }
            }
          } else if (typeof value === "object" && value.id) {
            queryParams.append(key, value.id);
          } else if (typeof value === "string" || typeof value === "number") {
            queryParams.append(key, String(value));
          }
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        navigate({ search: `?${queryString}` }, { replace: true });
      } else {
        navigate({ search: "" }, { replace: true });
      }
    };
    updateQueryParams();
  }, [params, navigate]);

  const handleRaiseRequest = (data: UrgentSourcingRaisedRequestForm) => {
    urgentSourcingStore.setIsLoading(true);
    for (let i = 0; i < data.products.length; i++) {
      const product = data.products[i];
      if (
        product.maxPriceRange &&
        product.minPriceRange &&
        product.maxPriceRange < product.minPriceRange
      ) {
        showError(
          "Error",
          `Max Price Range should be greater than Min Price Range for Product ${
            i + 1
          }`
        );
        urgentSourcingStore.setIsLoading(false);
        return;
      }
    }
    urgentSourcingStore
      .raisedRequest(data)
      .then((res) => {
        showSuccess("Request has been raised successfully!");
        urgentSourcingStore.managePopup("openRaiseRequest", "close");
        urgentSourcingStore.resetRaisedReqForm();
        urgentSourcingStore.getUrgentSourcingRequestListing(params);
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        urgentSourcingStore.setIsLoading(false);
      });
  };

  const handleApproveRequest = (data: UrgentSourcingAddProductForm) => {
    const hsn = data.hsn;
    if (hsn === undefined || hsn === "") {
      showError("Request failed", "Please enter HSN code");
      return;
    }

    if (data.hsn && data.hsn.length !== 6 && data.hsn.length !== 8) {
      showError("Request failed", "Invalid HSN code.");
      return;
    }

    if (hsn === "000000" || hsn === "00000000") {
      showError("Request failed", "Invalid HSN code.");
      return;
    }

    if (hsn === "123456" || hsn === "12345678") {
      showError("Request failed", "Invalid HSN code.");
      return;
    }

    if (hsn === "654321" || hsn === "87654321") {
      showError("Request failed", "Invalid HSN code.");
      return;
    }

    const hasRepeatingDigits = /^(\d)\1+$/.test(hsn || "");
    if (hasRepeatingDigits) {
      showError("Request failed", "Invalid HSN code.");
      return;
    }

    const phoneNumber = data.vendorPhoneNumber;
    if (
      phoneNumber === undefined ||
      phoneNumber === "" ||
      phoneNumber === null
    ) {
      showError("Request failed", "Please enter vendor phone number");
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phoneNumber)) {
      showError("Request failed", "Please enter a valid 10-digit phone number");
      return;
    }

    const requestId =
      urgentSourcingStore.addProductFormData.urgentSourcingRequestId;
    if (requestId) {
      urgentSourcingStore.setIsLoading(true);
      urgentSourcingStore
        .approveRequest(data)
        .then(() => {
          showSuccess("Request approved successfully");

          urgentSourcingStore.resetAddProductForm();
          urgentSourcingStore.managePopup("openAddProductPopup", "close");
        })

        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          );
        })
        .finally(() => {
          urgentSourcingStore.setIsLoading(false);
        });
    }
  };

  const handleCancelRequest = () => {
    const requestId =
      urgentSourcingStore.addProductFormData.urgentSourcingRequestId;
    if (requestId) {
      urgentSourcingStore.setIsLoading(true);
      urgentSourcingStore
        .cancelRequest(Number(requestId))
        .then(() => {
          showSuccess("Request cancelled successfully");
          urgentSourcingStore.resetCancelReason();
          urgentSourcingStore.managePopup("openCancelProductPopup", "close");
        })
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          );
        })
        .finally(() => {
          urgentSourcingStore.setIsLoading(false);
          urgentSourcingStore.managePopup("openCancelProductPopup", "close");
        });
    }
  };

  const handleSaveNewCategory = () => {
    urgentSourcingStore.isSavingCategory = true;
    urgentSourcingStore
      .addCategory(urgentSourcingStore.newCategoryName)
      .then(() => {
        urgentSourcingStore.isSavingCategory = false;
        urgentSourcingStore.handleCancelNewCategory();
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        urgentSourcingStore.isSavingCategory = false;
        urgentSourcingStore.handleCancelNewCategory();
      });
  };

  return (
    <Container
      sx={{
        paddingTop: 2,
      }}
      maxWidth="xl"
    >
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={urgentSourcingStore.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper>
        {userStore.hasPermission(
          EmployeeRoles.ADD_PRODUCT_IN_UNIVERSAL_CATALOG
        ) ? (
          <Box
            sx={{
              marginTop: "0.6rem",
              pt: 5,
              pl: 5,
              pr: 5,
              pb: 1,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: window.innerHeight - 110,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <CustomTypography variant="heading3" fontWeight={700}>
                Urgent Sourcing Requests
              </CustomTypography>
              <AppButton
                label="Raise a Request"
                variant="primary"
                onClick={() =>
                  urgentSourcingStore.managePopup("openRaiseRequest", "open")
                }
                customStyles={{ width: "160px" }}
              />
            </Box>

            <Box
              sx={{
                mt: 3,
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                gap: 4,
              }}
            >
              <CustomTextField
                placeholder={"Search by Lead Id, Product Name"}
                value={searchTerm}
                onSearch={(query) => handleSearch(query)}
                onKeyDown={handleKeyDown}
                width={"100%"}
              />
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <AppButton
                  variant="grayscale"
                  label="Filter"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={() => {
                    handleFilterToggle();
                  }}
                  customStyles={{ width: "130px" }}
                />
                {urgentSourcingStore.filterCount > 0 && (
                  <Badge
                    color="primary"
                    badgeContent={urgentSourcingStore.filterCount}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      transform: "translate(50%, -50%)",
                    }}
                  />
                )}
              </Box>
            </Box>

            {urgentSourcingStore.isFetchingData ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: window.innerHeight - 340,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <UrgentSourcingRequestTable
                maxHeight={window.innerHeight - 340}
                limit={params.limit || urgentSourcingStore.limit}
                page_no={params.page_no || 1}
                total_count={urgentSourcingStore.total_count}
                total_pages={urgentSourcingStore.total_pages}
                sortBy={params.sort_by || "created_date"}
                sortType={params.sort_type as SortType}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
                data={urgentSourcingStore.urgentSourcingRaisedReqResponce}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onDone={async (id: number) => {
                  urgentSourcingStore.updateAddProductField(
                    undefined,
                    undefined,
                    id
                  );
                  await urgentSourcingStore.getUrgentSourcingCategories();

                  urgentSourcingStore.managePopup(
                    "openAddProductPopup",
                    "open"
                  );
                }}
                onCancel={(id: number) => {
                  urgentSourcingStore.updateAddProductField(
                    undefined,
                    undefined,
                    id
                  );
                  urgentSourcingStore.managePopup(
                    "openCancelProductPopup",
                    "open"
                  );
                }}
              />
            )}

            <UrgentSourcingRaiseRequestPopUp
              open={urgentSourcingStore.popupState.openRaiseRequest}
              onClose={() => {
                urgentSourcingStore.managePopup("openRaiseRequest", "close");
                urgentSourcingStore.resetRaisedReqForm();
              }}
              onSave={(data) => handleRaiseRequest(data)}
            />

            <UrgentSourcingAddProductPopup
              categoriesList={urgentSourcingStore.urgentSourcingCategoriesResponse.map(
                (category) => ({
                  category_id: category.id,
                  category_name: category.category_name,
                })
              )}
              open={urgentSourcingStore.popupState.openAddProductPopup}
              onClose={() => {
                urgentSourcingStore.resetAddProductForm();
                urgentSourcingStore.managePopup("openAddProductPopup", "close");
              }}
              onSave={() =>
                handleApproveRequest(urgentSourcingStore.addProductFormData)
              }
              handleSaveNewCategory={() => handleSaveNewCategory()}
            />
            <>
              <Backdrop
                sx={(theme) => ({
                  color: theme.palette.customColors.white,
                  zIndex: 99999,
                })}
                open={urgentSourcingStore.isLoading}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
              <Dialog
                open={urgentSourcingStore.popupState.openCancelProductPopup}
                onClose={() => {
                  urgentSourcingStore.managePopup(
                    "openCancelProductPopup",
                    "close"
                  );
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "16px",
                      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                      width: "100%",
                      maxWidth: "500px",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      background: `linear-gradient(
                          90deg,
                          ${theme.palette.error.light} 0%,
                          ${theme.palette.error.main} 50%,
                          ${theme.palette.error.dark} 100%
                        )`,
                      borderTopLeftRadius: "16px",
                      borderTopRightRadius: "16px",
                    },
                  }}
                >
                  <DialogContent sx={{ pb: 0 }}>
                    <p> Are you sure you want to Reject this request ? </p>

                    <FormSmallTextField
                      placeholder="Enter Reject Reason*"
                      value={urgentSourcingStore.cancelReason || ""}
                      multiline
                      rows={3}
                      onChange={(id, e) =>
                        urgentSourcingStore.setCancelReason(e || "")
                      }
                      sx={{
                        mt: 2,
                      }}
                    />
                  </DialogContent>
                  <DialogActions
                    sx={{
                      padding: "10px 20px 10px 20px",
                      borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                      justifyContent: "flex-end",
                      gap: 1,
                    }}
                  >
                    <AppButton
                      variant="grayscale"
                      label="Cancel"
                      onClick={() => {
                        urgentSourcingStore.managePopup(
                          "openCancelProductPopup",
                          "close"
                        );
                      }}
                      customStyles={{
                        borderColor: theme.palette.customColors.greyDarkborder,
                        color: theme.palette.customColors.greyTextColor,
                        fontWeight: 600,
                        padding: "8px 20px",
                        borderRadius: "8px",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-1px)",
                        },
                      }}
                    />

                    <AppButton
                      onClick={handleCancelRequest}
                      variant="primary"
                      label="Submit"
                      disabled={
                        urgentSourcingStore.cancelReason === undefined ||
                        urgentSourcingStore.cancelReason === ""
                      }
                      customStyles={{
                        backgroundColor:
                          urgentSourcingStore.cancelReason !== undefined &&
                          urgentSourcingStore.cancelReason !== ""
                            ? theme.palette.customColors.EmeraldGreen
                            : theme.palette.customColors.greyDarkborder,
                        color: theme.palette.customColors.white,
                        fontWeight: 600,
                        padding: "8px 20px",
                        borderRadius: "8px",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor:
                            urgentSourcingStore.cancelReason !== undefined &&
                            urgentSourcingStore.cancelReason !== ""
                              ? theme.palette.customColors.greenDark
                              : theme.palette.customColors.greyDarkborder,
                          transform:
                            urgentSourcingStore.cancelReason !== undefined &&
                            urgentSourcingStore.cancelReason !== ""
                              ? "translateY(-1px)"
                              : "none",
                          boxShadow:
                            urgentSourcingStore.cancelReason !== undefined &&
                            urgentSourcingStore.cancelReason !== ""
                              ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                              : "none",
                        },
                        "&:disabled": {
                          backgroundColor:
                            theme.palette.customColors.greyDarkborder,
                          color: theme.palette.customColors.white,
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </DialogActions>
                </Box>
              </Dialog>
            </>
            {urgentSourcingStore.popupState.openFilter && (
              <FilterSidebar
                isOpen={urgentSourcingStore.popupState.openFilter}
                items={filterMenu}
                onApply={handleApplyFilters}
                handleClear={handleClearFilters}
                toggleFilter={handleFilterToggle}
              />
            )}
          </Box>
        ) : (
          <PermissionBlockLabel />
        )}
      </Paper>
    </Container>
  );
};
export default observer(UrgentSourcingRequestListing);
